import {genericDelete, genericGetAll, genericGetOne} from "../generic/crud";
import {ENTITY_ID_RAW_PURCHASE} from "../generic/Constants";
import {showConfirmDialog} from "../../generic-components/ConfirmDialog";
import {showToast, TOAST_FAILURE, TOAST_SUCCESS} from "../../utils/toast/Toast";
import {QR_SOURCE_AUTO} from "./qrReasonLabels";

// Second server-side filter for /api/generic/all (used together with isPartialRecord=true).
// Draft Purchases = QR drafts only; Pending Purchases = every other partial purchase (manual 2-step).
export const QR_DRAFT_FILTERS = {filterColumn2: "source", filterData2: QR_SOURCE_AUTO, filterOp2: "eq"};
export const NOT_QR_DRAFT_FILTERS = {filterColumn2: "source", filterData2: QR_SOURCE_AUTO, filterOp2: "ne"};

// Number of QR drafts waiting on this device (for the sidebar badge).
export async function fetchQrDraftCount() {
    const {data} = await genericGetAll(ENTITY_ID_RAW_PURCHASE, {
        currentIndex: 0,
        sizePerPage: 1,
        fixedFilterColumn: "isPartialRecord",
        fixedFilterData: true,
        ...QR_DRAFT_FILTERS,
    });
    return Number(data?.totalCount) || 0;
}

// A QR draft = scanner-created purchase that is still pending (never completed, never pushed to the server).
export function isQrDraft(purchase) {
    return !!purchase && purchase.source === QR_SOURCE_AUTO && purchase.isPartialRecord === true && purchase.deleted !== true;
}

// Loads one purchase; resolves null when it no longer exists (400 "Not found!", 404 or empty body).
// Network / server errors are re-thrown so a hiccup is not mistaken for a discarded purchase.
export async function fetchPurchaseOrNull(purchaseId) {
    try {
        const {data} = await genericGetOne(ENTITY_ID_RAW_PURCHASE, purchaseId);
        return data && typeof data === "object" && data.id && data.deleted !== true ? data : null;
    } catch (e) {
        const status = e?.response?.status;
        if (status === 400 || status === 404) return null;
        throw e;
    }
}

// Pending (partial) purchases as Map id -> purchase, used by the scan log to decide which actions to show.
export async function fetchPendingPurchases() {
    const {data} = await genericGetAll(ENTITY_ID_RAW_PURCHASE, {
        currentIndex: 0,
        sizePerPage: 1000,
        fixedFilterColumn: "isPartialRecord",
        fixedFilterData: true,
    });
    const list = Array.isArray(data?.entities) ? data.entities : [];
    return new Map(list.map(p => [p.id, p]));
}

/**
 * Asks for confirmation, re-checks the record is still a QR draft, then deletes it locally.
 * Resolves true when the draft was discarded.
 */
export async function confirmAndDiscardQrDraft(purchaseId) {
    if (!purchaseId) return false;
    const confirmed = await showConfirmDialog(
        "Discard this QR draft purchase? It will be removed and cannot be recovered."
    );
    if (!confirmed) return false;

    let purchase;
    try {
        purchase = await fetchPurchaseOrNull(purchaseId);
    } catch (e) {
        showToast("Could not reach Revcon. Please try again.", TOAST_FAILURE);
        return false;
    }
    if (!purchase) {
        showToast("This draft no longer exists.", TOAST_FAILURE);
        return true; // already gone: let the caller refresh
    }
    if (!isQrDraft(purchase)) {
        showToast(purchase.isPartialRecord === true
            ? "Only purchases created by a QR scan can be discarded here."
            : "This purchase has already been completed and cannot be discarded.", TOAST_FAILURE);
        return false;
    }
    try {
        await genericDelete(ENTITY_ID_RAW_PURCHASE, purchaseId);
        showToast("Draft discarded", TOAST_SUCCESS);
        return true;
    } catch (e) {
        const msg = typeof e?.response?.data === "string" ? e.response.data : "Could not discard the draft. Please try again.";
        showToast(msg, TOAST_FAILURE);
        return false;
    }
}
